import React, { memo } from 'react';
import { Marker, Popup } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import L from 'leaflet';
import PinCard from './PinCard';
import './Cluster.css';
import './Map.css';

// Function to create cluster icon
const createClusterIcon = (cluster) => {
  const count = cluster.getChildCount();
  let size = 'small';

  if (count > 50) size = 'large';
  else if (count > 20) size = 'medium';

  return L.divIcon({
    html: `
      <div class="cluster-pin ${size}">
        <span>${count}</span>
      </div>
    `,
    className: 'custom-cluster-icon',
    iconSize: L.point(40, 40, true),
  });
};

// Function to create custom icons based on style, likes, and age
const createCustomIcon = (style = 'default', likesCount = 0, createdAt, expiresAt) => {
  // Calculate size based on Logarithmic Scale
  // Base 28px.
  // 10 likes => 28 + log(11)*6 ~= 42px
  // 100 likes => 28 + log(101)*6 ~= 55px
  // 1000 likes => 28 + log(1001)*6 ~= 69px
  const baseSize = 28;
  const growthFactor = 6;
  const maxSize = 80; // Should rarely be hit

  const sizeIncrease = Math.min(Math.log(likesCount + 1) * growthFactor, maxSize - baseSize);
  const size = baseSize + sizeIncrease;

  // Determine Tier Class
  let tierClass = '';
  if (likesCount >= 500) tierClass = 'tier-legendary';
  else if (likesCount >= 100) tierClass = 'tier-famous';
  else if (likesCount >= 50) tierClass = 'tier-hot';
  else if (likesCount >= 10) tierClass = 'tier-rising';

  // TEMPORARILY DISABLED: expiration-based marker fading
  // const { opacity } = getExpiryStatus(expiresAt);
  const opacity = 1;

  // Style configurations
  const styleConfigs = {
    default: { color: '#3B82F6', icon: '📍' },
    heart: { color: '#F43F5E', icon: '❤️' },
    star: { color: '#F59E0B', icon: '⭐' },
    fire: { color: '#EF4444', icon: '🔥' },
    leaf: { color: '#10B981', icon: '🍃' },
    moon: { color: '#8B5CF6', icon: '🌙' },
    coffee: { color: '#92400E', icon: '☕' },
    party: { color: '#A855F7', icon: '🎉' },
    music: { color: '#D946EF', icon: '🎵' },
    food: { color: '#F97316', icon: '🍔' },
    basketball: { color: '#F97316', icon: '🏀' },
    soccer: { color: '#10B981', icon: '⚽' },
    gym: { color: '#64748B', icon: '💪' },
    gaming: { color: '#8B5CF6', icon: '🎮' },
    study: { color: '#0EA5E9', icon: '📚' },
  };

  const config = styleConfigs[style] || styleConfigs.default;

  return new L.DivIcon({
    html: `
      <div style="
        width: ${size}px;
        height: ${size}px;
        position: relative;
        opacity: ${opacity};
        filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
        transition: all 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      " class="${tierClass}">
        <div style="
          font-size: ${size * 0.7}px;
          line-height: ${size}px;
          text-align: center;
        ">${config.icon}</div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [size, size],
    iconAnchor: [size / 2, size],
    popupAnchor: [0, -size],
  });
};

const PlaceMarkers = ({ places, clusteringEnabled, markerRefs, onDeleted, onUpdated }) => {
  // Render markers function to avoid duplication
  const renderMarkers = () => {
    return places.map((place) => (
      <Marker
        key={place.id}
        position={[place.lat, place.lng]}
        icon={createCustomIcon(
          place.style || 'default',
          place.likes_count || 0,
          place.created_at,
          place.expires_at
        )}
        ref={(ref) => {
          if (ref && markerRefs && markerRefs.current) {
            markerRefs.current[place.id] = ref;
          }
        }}
      >
        <Popup>
          <PinCard place={place} onDeleted={onDeleted} onUpdated={onUpdated} />
        </Popup>
      </Marker>
    ));
  };

  if (clusteringEnabled) {
    return (
      <MarkerClusterGroup
        chunkedLoading
        iconCreateFunction={createClusterIcon}
        maxClusterRadius={60}
        spiderfyOnMaxZoom={true}
        showCoverageOnHover={false}
      >
        {renderMarkers()}
      </MarkerClusterGroup>
    );
  }

  return <>{renderMarkers()}</>;
};

// Optimize performance by memoizing the component
// It will only re-render if props change (places, clusteringEnabled, etc.)
export default memo(PlaceMarkers);
